import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, Medal, RefreshCw } from 'lucide-react';
import {
  appliquerPreset,
  fetchCatalogueSportif,
  fetchPresetClub,
  type ConflitsPreset,
  type DisciplineCatalogue,
  type TermeDiscipline,
} from '../services/api';
import ReglagesPreset from './ReglagesPreset';
import FicheFederation from './FicheFederation';
import LienFederation from './LienFederation';

/**
 * Le choix de la discipline, et ce qu'il apporte au club.
 *
 * Configurer un registre sans point de départ, c'est saisir à la main les neuf
 * catégories d'âge de sa fédération, ses postes, ses champs de licence et ses
 * courriers — un après-midi de travail, refait par chaque club, à l'identique,
 * pour la même fédération. Le préréglage fait ce travail ; le club le corrige
 * ensuite là où sa réalité diffère.
 *
 * CE QU'UN PRÉRÉGLAGE N'EST PAS
 * ---------------------------------------------------------------------------
 * Une contrainte. Tout se renomme, s'ajoute et se supprime après coup, et ce
 * que le club a réécrit survit à une reprise du préréglage. L'écran le dit
 * plutôt que de le laisser deviner : un responsable qui craint de perdre son
 * travail ne clique pas.
 */
export default function ChoixDiscipline() {
  const client = useQueryClient();
  const [choisie, setChoisie] = useState<string | null>(null);
  const [conflits, setConflits] = useState<ConflitsPreset | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const catalogue = useQuery({ queryKey: ['catalogue-sportif'], queryFn: fetchCatalogueSportif });
  const preset = useQuery({ queryKey: ['preset-club'], queryFn: fetchPresetClub });

  const application = useMutation({
    mutationFn: ({ sport, confirme }: { sport: string; confirme: boolean }) =>
      appliquerPreset(sport, { confirme }),
    onSuccess: (reponse: { success?: boolean; conflits?: ConflitsPreset; message?: string }) => {
      // Le serveur refuse en 409 quand le club a personnalisé quelque chose, et
      // rend le détail. On le montre au lieu de réessayer d'autorité.
      if (reponse?.success === false && reponse.conflits) {
        setConflits(reponse.conflits);
        return;
      }
      setConflits(null);
      setErreur(null);
      setChoisie(null);
      void client.invalidateQueries({ queryKey: ['preset-club'] });
      void client.invalidateQueries({ queryKey: ['glossaire-sportif'] });
      void client.invalidateQueries({ queryKey: ['adherents-configuration'] });
    },
    onError: (e: Error) => setErreur(e.message),
  });

  if (catalogue.isPending || preset.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const disciplines = catalogue.data?.disciplines ?? [];

  /*
   * TOUTES les disciplines en service, et non « la » discipline.
   *
   * Un club omnisports en gère plusieurs de front : le football, la
   * gymnastique et la pétanque, chacune avec sa fédération, ses catégories
   * d'âge et son numéro d'affiliation. L'écran en montre donc une fiche par
   * discipline.
   *
   * Sans le module, la liste n'en contient qu'une, et l'écran est celui qu'on
   * connaît. C'est la même page pour les deux cas : un club qui souscrit le
   * module ne change pas d'écran, il voit une seconde fiche apparaître.
   */
  const enService = preset.data?.presets ?? [];
  const multi = preset.data?.multiDisciplines ?? false;
  const clesEnService = new Set(enService.map((p) => p.sport));

  /*
   * La saison décide, et l'écran le dit AVANT le clic.
   *
   * Tant que des licenciés sont inscrits pour la saison courante, changer de
   * discipline ferait disparaître les catégories dans lesquelles ils sont
   * rangés — et aucune correspondance n'existe entre les catégories de deux
   * disciplines, parce qu'il n'y en a pas dans le monde réel.
   *
   * Le refus se lit donc en tête, avec la date à laquelle ce sera de nouveau
   * possible. Un refus sans horizon envoie chercher un réglage qui n'existe
   * pas.
   */
  const saisonOuverte = preset.data?.saison?.ouvert ?? true;
  const motifSaison = preset.data?.saison?.motif ?? null;

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Medal className="w-5 h-5 text-primary-600" aria-hidden="true" />
          {multi ? 'Vos disciplines' : 'Votre discipline'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Choisir votre fédération remplit d'un coup vos catégories d'âge, vos postes, les champs de
          la fiche de licencié et vos courriers types. Tout reste modifiable ensuite — et ce que vous
          aurez réécrit ne sera jamais écrasé.
        </p>
        {/*
          La phrase du module, dite une fois et à l'endroit où elle sert.

          Elle répond à la question qu'on se pose devant les quinze cartes quand
          on en a déjà choisi une : « si je clique, est-ce que je perds
          l'autre ? ». Sans le module, la réponse est oui ; avec, elle est non.
          C'est exactement l'inverse, et le deviner coûte une saison.
        */}
        {multi && (
          <p className="text-sm text-primary-800 dark:text-primary-300 mt-1">
            Votre association est <strong>omnisports</strong> : choisir une nouvelle discipline
            l'<strong>ajoute</strong> à celles que vous gérez déjà, sans toucher aux autres. Un même
            adhérent peut prendre une licence dans plusieurs d'entre elles la même saison.
          </p>
        )}
      </header>

      {erreur !== null && (
        <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{erreur}</p>
      )}

      {conflits !== null && choisie !== null && (
        <Confirmation
          conflits={conflits}
          discipline={disciplines.find((d) => d.cle === choisie)?.nom ?? choisie}
          enCours={application.isPending}
          onAnnuler={() => {
            setConflits(null);
            setChoisie(null);
          }}
          onConfirmer={() => application.mutate({ sport: choisie, confirme: true })}
        />
      )}

      {/*
        La fiche de la fédération, avant la liste des disciplines.

        Quand la saison tient la discipline, la première chose à savoir est
        qu'elle la tient — sinon on parcourt quinze cartes avant de comprendre
        qu'aucune n'est cliquable. Et quand elle ne la tient pas, la fiche reste
        utile : c'est la page qu'on rouvrira dans six mois pour répondre à « nos
        catégories viennent d'où ? ».
      */}
      {enService.map((p) => (
        <FicheFederation
          key={p.id}
          preset={p}
          discipline={disciplines.find((d) => d.cle === p.sport)}
          /*
            La saison ne tient une discipline que dans un club mono-discipline.
            Ajouter la gymnastique à côté du football ne déplace personne :
            les footballeurs gardent leurs catégories, la gymnastique part de
            zéro. La fiche n'a donc pas à annoncer une porte fermée qui ne
            l'est pas.
          */
          saisonOuverte={multi ? true : saisonOuverte}
          motifSaison={multi ? null : motifSaison}
          fermable={multi && enService.length > 1}
        />
      ))}

      {/*
        Les quinze cartes restent affichées, saison ouverte ou non.

        Les cacher priverait le club de ce qui reste utile quand la porte est
        fermée : comparer, préparer la saison suivante, lire le vocabulaire
        d'une autre fédération. Seul le CLIC est retiré, sur celles qui
        déplaceraient des licenciés.
      */}
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {disciplines.map((discipline) => (
          <Carte
            key={discipline.cle}
            discipline={discipline}
            enService={clesEnService.has(discipline.cle)}
            ajoute={multi}
            /*
              Reprendre la discipline DÉJÀ en service reste possible en pleine
              saison : rien ne bouge, les catégories gardent leurs identifiants,
              personne n'est déplacé. C'est même ce qu'on fait pour profiter
              d'une correction du catalogue.
            */
            /*
              Avec le module, aucune carte n'est figée : cliquer AJOUTE une
              discipline, ce qui ne déplace aucun licencié.
            */
            fige={!multi && !saisonOuverte && !clesEnService.has(discipline.cle)}
            occupe={application.isPending && choisie === discipline.cle}
            onChoisir={() => {
              setChoisie(discipline.cle);
              setErreur(null);
              application.mutate({ sport: discipline.cle, confirme: false });
            }}
          />
        ))}
      </ul>

      {/*
        L'écran d'adaptation, sous le choix et non ailleurs.

        Le préréglage et sa retouche sont le même sujet : « voici ce que votre
        fédération propose, voici ce que vous en faites ». Les séparer en deux
        pages obligerait à chercher, et un réglage qu'on cherche est un réglage
        qu'on n'utilise pas.

        Il reste ouvert même quand la saison tient la discipline : renommer une
        catégorie ne déplace personne.
      */}
      {enService.map((p) => (
        <ReglagesPreset key={p.id} preset={p} />
      ))}
    </section>
  );
}

/**
 * Ce que l'application toucherait, dit avant de le faire.
 *
 * Le serveur a refusé et rendu le détail ; on le montre tel quel. Un
 * avertissement qui ne chiffre rien — « des données seront modifiées » — ne
 * permet pas de décider, et l'on clique au hasard.
 */
function Confirmation({
  conflits,
  discipline,
  enCours,
  onAnnuler,
  onConfirmer,
}: {
  conflits: ConflitsPreset;
  discipline: string;
  enCours: boolean;
  onAnnuler: () => void;
  onConfirmer: () => void;
}) {
  const personnalisees = Object.entries(conflits.personnalisees).filter(([, n]) => n > 0);

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
      <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
        {conflits.changeDeDiscipline
          ? `Votre club est configuré en ${conflits.disciplineActuelle}. Passer à ${discipline} ?`
          : `Reprendre le préréglage ${discipline} ?`}
      </p>

      {personnalisees.length > 0 ? (
        <>
          <p className="mt-2 text-sm text-amber-900/90">
            Vous avez modifié {conflits.total} élément{conflits.total > 1 ? 's' : ''} :
          </p>
          <ul className="mt-1 flex flex-wrap gap-x-4 text-sm text-amber-900/90">
            {personnalisees.map(([famille, n]) => (
              <li key={famille}>
                <strong>{n}</strong> {famille}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-amber-900/90">
            Ces éléments sont <strong>conservés</strong>. Seul ce qui est encore tel que nous
            l'avions livré sera remis à jour.
          </p>
        </>
      ) : (
        <p className="mt-2 text-sm text-amber-900/90">
          L'ancienne configuration est conservée, désactivée : rien n'est détruit.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onConfirmer}
          disabled={enCours}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-700 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60 min-h-[44px]"
        >
          {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Continuer
        </button>
        <button
          type="button"
          onClick={onAnnuler}
          disabled={enCours}
          className="rounded-lg border border-amber-300 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 min-h-[44px]"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function Carte({
  discipline,
  enService,
  ajoute,
  fige,
  occupe,
  onChoisir,
}: {
  discipline: DisciplineCatalogue;
  enService: boolean;
  /**
   * Cliquer ajoutera-t-il, au lieu de remplacer ?
   *
   * Le survol le dit, parce que c'est la seule question qui compte devant une
   * carte quand on en a déjà choisi une.
   */
  ajoute: boolean;
  /** La saison en cours interdit d'aller vers cette discipline-là. */
  fige: boolean;
  occupe: boolean;
  onChoisir: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onChoisir}
        disabled={occupe || fige}
        aria-current={enService ? 'true' : undefined}
        title={
          fige
            ? 'Impossible tant que la saison en cours porte des licenciés'
            : enService
              ? 'Déjà en service — reprendre le préréglage sans rien perdre'
              : ajoute
                ? `Ajouter ${discipline.nom} à vos disciplines`
                : undefined
        }
        className={`w-full h-full text-left rounded-xl border p-3 transition min-h-[44px] ${
          enService
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-800'
        } disabled:opacity-60`}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{discipline.nom}</span>
          {occupe ? (
            <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />
          ) : enService ? (
            <Check className="w-4 h-4 text-primary-600 shrink-0" aria-label="en service" />
          ) : null}
        </span>

        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{discipline.federation}</span>

        <span className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
          {discipline.categories} catégories · {discipline.termes} termes
        </span>

        <MotsDeLaDiscipline mots={discipline.apercu} />
      </button>

      {/*
        HORS DU BOUTON, ET C'EST OBLIGATOIRE.

        Un lien dans un bouton est du balisage invalide : le navigateur défait
        l'imbrication comme il peut, et le résultat diffère d'un moteur à
        l'autre. Il est donc frère de la carte, posé sous elle.

        Sa fonction n'est pas décorative : on compare des disciplines avant
        d'en adopter une, et « à quoi cette fédération m'engage-t-elle ? » est
        exactement la question qu'on se pose à ce moment-là.
      */}
      {discipline.reglementation ? (
        <p className="mt-1.5 px-1">
          <LienFederation url={discipline.reglementation} />
        </p>
      ) : null}
    </li>
  );
}

/**
 * Trois mots que cette discipline est seule à employer.
 *
 * CE QUI S'AFFICHAIT AVANT
 * ---------------------------------------------------------------------------
 * Les quinze cartes portaient la même aide, sur le même mot écrit en dur :
 * « Licence ». Et la définition venait du glossaire de la discipline DÉJÀ en
 * service — un club en judo lisait donc, sur la carte « Rugby », la définition
 * judo du mot licence. L'aide ne disait rien de la discipline qu'on
 * s'apprêtait à choisir, qui est pourtant la seule question qu'on se pose
 * devant cette carte.
 *
 * Les mots viennent maintenant du catalogue, calculés côté serveur : ceux qui
 * n'apparaissent que dans une discipline. « Ippon » pour le judo, « carreau »
 * pour la pétanque, « galop » pour l'équitation. On reconnaît la sienne d'un
 * coup d'œil, ce qui est tout ce qu'on demande à cet écran.
 *
 * Pas de bulle d'aide ici : la carte est déjà un bouton, et une aide cliquable
 * dans un bouton donne deux cibles superposées — sur un téléphone, on ne sait
 * plus laquelle on touche. La définition est donc en attribut « title », qui
 * s'affiche au survol sans rien capturer.
 */
function MotsDeLaDiscipline({ mots }: { mots: TermeDiscipline[] }) {
  if (mots.length === 0) {
    return null;
  }

  return (
    <span className="mt-1.5 flex flex-wrap gap-1">
      {mots.map((mot) => (
        <span
          key={mot.terme}
          title={mot.definition}
          className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-300"
        >
          {mot.terme}
        </span>
      ))}
    </span>
  );
}
